import React, {useState, useEffect, useCallback} from 'react'
/** @jsxImportSource @emotion/react */
import {css} from '@emotion/react'
import {connect} from 'react-redux'

import useMediaQuery from '@mui/material/useMediaQuery'
import {
  format, startOfDay, endOfDay, isThisYear, isSameDay, isToday as dateFnsIsToday 
} from 'date-fns'

import {
  initiateSearch, 
  updateDateFilter,
  clearFilter
} from '../../actions'

import Button from '@mui/material/Button'
import ButtonGroup from '@mui/material/ButtonGroup'

import Calendar from '../generics/calendar/Calendar'
import FilterHeader from './FilterHeader'
import FilterFooter from './FilterFooter'



const cssStyles = ({padding, isSmallScreen}) => ({
  body: css`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    padding: ${padding || '20px 30px'}
  `,
  buttonContainer: css`
    padding: 10px 20px 0px 20px;
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: flex-end;
  `,
  button: css`
    max-height: 35px;
    text-transform: uppercase;
    font-size: 12px;
    height: 35px;
  `,
  captionContainer: css`
    height: 40px;
    padding-bottom: 20px;

    p {
      text-align: center;
    }
    strong {
      font-weight: 600;
    }
  `,
  footerContainer: css`
    width: 100%;
    background-color: #fff;
    z-index: 50;
  `,
})

// A helper function to get a consistent button style based on active state
function getButtonStyle(isActive) {
  return {
    border: isActive ? '2px solid rgb(62, 166, 255)' : null,
    backgroundColor: isActive ? 'rgba(62, 166, 255, 0.1)' : '#f5f5f5',
    '&:hover': {
      border: isActive ? '2px solid rgb(62, 166, 255)' : null,
      backgroundColor: isActive ? 'rgba(62, 166, 255, 0.1)' : '#f5f5f5'
    }
  }
}

const DateFilter = ({
  close,
  time,
  padding,
  callback,
  hideHeader,
  hideFooter,
  clearFilter,
  initiateSearch,
  updateDateFilter
}) => {
  const isSmallScreen = useMediaQuery(theme => theme.breakpoints.down('sm'))
  const styles = cssStyles({padding, isSmallScreen})

  // State
  const [start, setStart] = useState(time.start)
  const [end, setEnd] = useState(time.end)
  const [isActive, setIsActive] = useState(time.isDateActive)

  const [isToday, setIsToday] = useState(dateFnsIsToday(start) && !end && isActive)
  const [isRange, setIsRange] = useState(start && end && !isSameDay(start, end))


  // Effects
  useEffect(() => {
    if (callback) callback(start, end)
  }, [start, end, callback])



  useEffect(() => {
    // If the original time.start isn't today or date filter isn't active, disable isToday
    if (!dateFnsIsToday(time.start) || !isActive) {
      setIsToday(false)
    }
  }, [time.start, isActive])



  // Handlers
  const toggleRange = useCallback(() => {
    if (end) setEnd(null)

    // If we are enabling range mode, we cannot have "today" mode
    if (!isRange) setIsToday(false)

    setIsRange(prev => !prev)
  }, [end, isRange])



  const toggleToday = useCallback(() => {
    if (!isToday) {
      // Set the date to today when activating
      setIsRange(false)
      setIsActive(true)

      const today = new Date()
      setStart(startOfDay(today))
      setEnd(endOfDay(today))
    }
    
    setIsToday(prev => !prev)
  }, [isToday])



  const updateTimeHandler = useCallback(({start, end}) => {
    setStart(start)
    setEnd(end)
    setIsActive(true)
  }, [])



  const apply = useCallback(() => {
    // Only apply if there are changes
    const timesChanged = time.start?.getTime() !== start?.getTime() || time.end?.getTime() !== end?.getTime()
    
    if (!isActive || timesChanged) {
      updateDateFilter({ start, end })
      initiateSearch()
      close()
    }
  }, [isActive, start, end, time.start, time.end, updateDateFilter, initiateSearch, close])



  const clearDateFilter = useCallback(() => {
    setIsActive(false)
    setStart(new Date())
    setEnd(null)

    if (time.isDateActive) {
      clearFilter('DATE')
      initiateSearch()
      close()
    }
  }, [clearFilter, initiateSearch, close, time.isDateActive])



  const dateString = date => {
    return isThisYear(date) ? format(date, 'MMMM d') : format(date, 'MMMM d, y')
  }



  return (
    <div>
      {!hideHeader && 
        <FilterHeader title='Select Date' clear={clearDateFilter} close={close} />
      }

      <div css={styles.body}>
        <div css={styles.buttonContainer}>
          <ButtonGroup size='small'>
            <Button 
              css={styles.button} 
              onClick={toggleRange}
              sx={getButtonStyle(isRange)}
            >
              Range
            </Button>

            <Button 
              css={styles.button} 
              onClick={toggleToday}
              sx={getButtonStyle(isToday)}
            >
              Today
            </Button>
          </ButtonGroup>
        </div>

        <Calendar 
          start={start}
          end={end}
          isRange={isRange}
          isToday={isToday}
          isActive={isActive}
          callback={updateTimeHandler}
        />

        <div css={styles.captionContainer}>
          {isRange && end && (
            <p>
              From <strong>{dateString(start)}</strong> to <strong>{dateString(end)}</strong>
            </p>
          )}
          {!isRange && <p><strong>{dateString(start)}</strong></p>}
        </div>
      </div>

      <div css={styles.footerContainer}>
        {!hideFooter && <FilterFooter onClick={apply} />}
      </div>
    </div>
  )
}



const mapStateToProps = state => {
  return {
    time: state.search.filters.time
  }
}


const actions = {clearFilter, initiateSearch, updateDateFilter}


export default connect(mapStateToProps, actions)(DateFilter)



